/**
 * Widget compact "Mon usage du mois" — affiché dans les dashboards Cooper/Membre.
 * Affiche : solde, consommation, top 3 actions, suggestion de pack.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Coins, TrendingDown, Sparkles, ArrowRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const MonUsageCreditsWidget = ({ getAuthHeader, linkTo = '/mes-consommations' }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/credits/me/usage`, { headers: getAuthHeader() });
        if (alive) setData(res.data);
      } catch {
        // Best-effort widget : pas de toast, juste pas d'affichage
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [getAuthHeader]);

  if (loading) {
    return (
      <Card data-testid="mon-usage-widget-loading">
        <CardContent className="py-6 text-sm text-slate-400">Chargement de votre consommation…</CardContent>
      </Card>
    );
  }
  if (!data?.success) return null;

  const { totaux, solde, top_actions = [], suggestions = {} } = data;
  const pack = suggestions.pack_recommande;
  const showSuggestion = pack && (pack.couvre_le_besoin || suggestions.besoin_estime_mois > solde.total);

  return (
    <Card className="border-l-4 border-l-indigo-500 shadow-sm" data-testid="mon-usage-widget">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Coins className="h-5 w-5 text-indigo-500" />
              Mon usage du mois
            </CardTitle>
            <CardDescription className="text-xs">
              {data.periode?.label || 'Sur les 30 derniers jours'}
            </CardDescription>
          </div>
          <Badge variant="outline" className="text-emerald-700 border-emerald-200">
            Solde : {solde.total} cr.
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="rounded-lg bg-slate-50 p-2">
            <p className="text-[10px] uppercase tracking-wide text-slate-500">Consommés</p>
            <p className="text-xl font-bold text-red-500 leading-tight">{totaux.credits_consommes}</p>
          </div>
          <div className="rounded-lg bg-slate-50 p-2">
            <p className="text-[10px] uppercase tracking-wide text-slate-500">Émis</p>
            <p className="text-xl font-bold text-emerald-600 leading-tight">{totaux.credits_emis}</p>
          </div>
          <div className="rounded-lg bg-slate-50 p-2">
            <p className="text-[10px] uppercase tracking-wide text-slate-500">Actions</p>
            <p className="text-xl font-bold text-slate-700 leading-tight">{totaux.nb_actions_distinctes}</p>
          </div>
        </div>

        {top_actions.length > 0 ? (
          <div>
            <p className="text-[11px] font-semibold text-slate-500 uppercase mb-1">Top actions</p>
            <ul className="space-y-1">
              {top_actions.slice(0, 3).map((a) => (
                <li key={a.code} className="flex items-center justify-between text-xs px-2 py-1.5 rounded bg-slate-50">
                  <span className="truncate flex items-center gap-1">
                    <TrendingDown className="h-3 w-3 text-red-400" />
                    <span className="font-medium text-slate-700">{a.libelle}</span>
                    <span className="text-slate-400">×{a.nb_operations}</span>
                  </span>
                  <Badge variant="secondary" className="text-[11px]">{a.credits_consommes} cr.</Badge>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <p className="text-xs text-slate-400 italic text-center py-2">
            Pas encore de consommation ce mois-ci.
          </p>
        )}

        {showSuggestion && (
          <div className="rounded-lg p-3 bg-gradient-to-r from-indigo-50 to-violet-50 border border-indigo-100">
            <div className="flex items-start gap-2">
              <Sparkles className="h-4 w-4 text-indigo-500 mt-0.5" />
              <div className="flex-1">
                <p className="text-xs font-medium text-indigo-800">
                  Pack recommandé : <strong>{pack.nom}</strong>
                </p>
                <p className="text-[11px] text-indigo-600 mt-0.5">
                  {pack.credits} crédits {pack.prix_eur ? `pour ${pack.prix_eur} €` : ''} — couvrira votre rythme de consommation actuel.
                </p>
                <Link
                  to="/credits"
                  className="inline-flex items-center gap-1 text-[11px] text-indigo-700 hover:text-indigo-900 mt-1"
                  data-testid="mon-usage-widget-buy-pack"
                >
                  Voir les packs <ArrowRight className="h-3 w-3" />
                </Link>
              </div>
            </div>
          </div>
        )}

        <div className="pt-1 border-t">
          <Button asChild variant="ghost" size="sm" className="w-full text-indigo-700 hover:bg-indigo-50" data-testid="mon-usage-widget-detail-link">
            <Link to={linkTo}>
              Voir le détail de mes consommations <ArrowRight className="h-3.5 w-3.5 ml-1" />
            </Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default MonUsageCreditsWidget;
